import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Save, X, ChevronLeft } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL 
  ? `${process.env.REACT_APP_BACKEND_URL}/api` 
  : '/api';

export default function EditCase() {
  const { caseId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [caseData, setCaseData] = useState(null);
  const [formData, setFormData] = useState({
    case_number: '',
    year: '',
    case_type: '',
    court: '',
    department: '',
    status: '',
    parties: [{ name: '', role: '' }],
    next_hearing_date: '',
    next_hearing_notes: ''
  });

  useEffect(() => {
    fetchCase();
  }, [caseId]);

  const fetchCase = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/cases/${caseId}`);
      const data = response.data;
      setCaseData(data);
      
      setFormData({
        case_number: data.case_number || '',
        year: data.year || '',
        case_type: data.case_type || '',
        court: data.court || '',
        department: data.department || '',
        status: data.status || '',
        parties: data.parties && data.parties.length > 0 ? data.parties : [{ name: '', role: '' }],
        next_hearing_date: data.next_hearing?.date ? new Date(data.next_hearing.date).toISOString().slice(0, 16) : '',
        next_hearing_notes: data.next_hearing?.notes || ''
      });
    } catch (error) {
      toast.error('فشل تحميل بيانات القضية');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setSaving(true);
      
      const updateData = {
        case_number: formData.case_number,
        year: formData.year,
        case_type: formData.case_type,
        court: formData.court,
        department: formData.department,
        status: formData.status,
        parties: formData.parties.filter(p => p.name && p.role),
      };
      
      if (formData.next_hearing_date) {
        updateData.next_hearing = {
          date: new Date(formData.next_hearing_date).toISOString(),
          notes: formData.next_hearing_notes
        };
      }
      
      await axios.put(`${API}/cases/${caseId}`, updateData);
      toast.success('تم حفظ التعديلات بنجاح');
      navigate(`/cases/${caseId}`);
    } catch (error) {
      toast.error('فشل حفظ التعديلات');
    } finally {
      setSaving(false);
    }
  };

  const addParty = () => {
    setFormData({
      ...formData,
      parties: [...formData.parties, { name: '', role: '' }]
    });
  };

  const removeParty = (index) => {
    const newParties = formData.parties.filter((_, i) => i !== index);
    setFormData({ ...formData, parties: newParties });
  };

  const updateParty = (index, field, value) => {
    const newParties = [...formData.parties];
    newParties[index][field] = value;
    setFormData({ ...formData, parties: newParties });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(`/cases/${caseId}`)}
            className="hover:bg-gray-100"
          >
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">تعديل القضية</h1>
            <p className="text-gray-500 mt-1">الرقم الآلي: {caseData?.auto_number}</p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <Card>
          <CardHeader>
            <CardTitle>المعلومات الأساسية</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="case_number">رقم القضية *</Label>
                <Input
                  id="case_number"
                  value={formData.case_number}
                  onChange={(e) => setFormData({ ...formData, case_number: e.target.value })}
                  placeholder="2025/12345"
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="year">السنة</Label>
                <Input
                  id="year"
                  value={formData.year}
                  onChange={(e) => setFormData({ ...formData, year: e.target.value })}
                  placeholder="2025"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="case_type">نوع القضية</Label>
                <Input
                  id="case_type"
                  value={formData.case_type}
                  onChange={(e) => setFormData({ ...formData, case_type: e.target.value })}
                  placeholder="مدني، تجاري، جنائي..."
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="status">الحالة *</Label>
                <select
                  id="status"
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">اختر الحالة</option>
                  <option value="منظورة">منظورة</option>
                  <option value="مؤجلة">مؤجلة</option>
                  <option value="محجوزة للحكم">محجوزة للحكم</option>
                  <option value="شطب">شطب</option>
                  <option value="صدر حكم">صدر حكم</option>
                </select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="court">المحكمة</Label>
                <Input
                  id="court"
                  value={formData.court}
                  onChange={(e) => setFormData({ ...formData, court: e.target.value })}
                  placeholder="محكمة الكويت الكلية"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="department">الدائرة</Label>
                <Input
                  id="department"
                  value={formData.department}
                  onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                  placeholder="الدائرة الأولى"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="mt-6">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>أطراف القضية</CardTitle>
              <Button type="button" onClick={addParty} size="sm" variant="outline">
                إضافة طرف
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {formData.parties.map((party, index) => (
              <div key={index} className="flex gap-3 items-start">
                <div className="flex-1 grid grid-cols-2 gap-3">
                  <Input
                    placeholder="الاسم"
                    value={party.name}
                    onChange={(e) => updateParty(index, 'name', e.target.value)}
                  />
                  <Input
                    placeholder="الصفة (مدعي، مدعى عليه...)"
                    value={party.role}
                    onChange={(e) => updateParty(index, 'role', e.target.value)}
                  />
                </div>
                {formData.parties.length > 1 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => removeParty(index)}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                )}
              </div>
            ))}
          </CardContent>
        </Card>

        <Card className="mt-6">
          <CardHeader>
            <CardTitle>الجلسة القادمة</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="next_hearing_date">تاريخ ووقت الجلسة</Label>
              <Input
                id="next_hearing_date"
                type="datetime-local"
                value={formData.next_hearing_date}
                onChange={(e) => setFormData({ ...formData, next_hearing_date: e.target.value })}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="next_hearing_notes">ملاحظات الجلسة</Label>
              <Textarea
                id="next_hearing_notes"
                value={formData.next_hearing_notes}
                onChange={(e) => setFormData({ ...formData, next_hearing_notes: e.target.value })}
                placeholder="جلسة مرافعة، لاستكمال البينة..."
                rows={3}
              />
            </div>
          </CardContent>
        </Card>

        <div className="flex gap-3 mt-6">
          <Button
            type="submit"
            disabled={saving}
            className="flex-1 bg-blue-600 hover:bg-blue-700 h-12 text-base"
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white ml-2"></div>
                جاري الحفظ...
              </>
            ) : (
              <>
                <Save className="w-5 h-5 ml-2" />
                حفظ التعديلات
              </>
            )}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate(`/cases/${caseId}`)}
            disabled={saving}
            className="h-12"
          >
            إلغاء
          </Button>
        </div>
      </form>
    </div>
  );
}